import { computed, ref, watch } from 'vue';
import { useComponentI18n } from '@rdrive/i18n';
import messages from './messages/index.js';

// The opt-in runner's result cap (standalone asset-management's MAX_ROWS).
const MAX_ROWS = 200;

// Arrow hands back BigInt, Date and typed-array values - render them readably.
// (standalone asset-management's formatCell, but for one object case: an Arrow value with its own
// `toString` — a HUGEINT such as `SUM(<BIGINT>)` arrives as a DecimalBigNum, whose JSON is the
// QUOTED string, so AM showed `"4151"` — prints that string; an object without one, a STRUCT,
// stays JSON)
const formatCell = (value) => {
  if (value === null || value === undefined) return '∅';
  if (typeof value === 'bigint') return value.toString();
  if (value instanceof Date) return value.toISOString().slice(0, 19).replace('T', ' ');
  if (typeof value === 'object') {
    const text = String(value);
    return text.startsWith('[object ') ? JSON.stringify(value) : text;
  }
  const text = String(value);
  return text.length > 120 ? `${text.slice(0, 117)}…` : text;
};

// What `query.run` resolved with → { columns, rows }. An array is rows (the columns are the
// first row's keys, as standalone AM read them); `{ columns, rows }` may name the columns.
const toResult = (result) => {
  const rows = Array.isArray(result) ? result : (result?.rows ?? []);
  const columns = !Array.isArray(result) && Array.isArray(result?.columns)
    ? result.columns
    : (rows.length > 0 ? Object.keys(rows[0]) : []);
  return { columns, rows };
};

export default (props, { emit }) => {
  // LOCAL composer with the warn flags off — see @rdrive/i18n for why the flags are
  // assigned rather than passed.
  const { t } = useComponentI18n(messages);

  const dialog = computed({
    get: () => props.modelValue,
    set: (value) => emit('update:modelValue', value),
  });

  // Close the dialog
  const close = () => {
    dialog.value = false;
  };

  // --- the opt-in SQL runner (`query`; AM_ABSORPTION_PLAN F1) ------------------------------
  // Standalone asset-management's ad-hoc runner — the fastest way to sanity check a data model
  // against real data. Everything that touches DuckDB is the app's `query.run`; this only keeps
  // the text, the state of the last run and its table. Unused (and never rendered) without
  // `query`.
  const sql = ref(props.query?.initialSql ?? '');
  // An app that hands the runner over only later still gets its first text — never over the
  // user's own.
  watch(() => props.query?.initialSql, (text) => {
    if (!sql.value && text) sql.value = text;
  });
  const running = ref(false);
  const error = ref('');
  const noRows = ref(false);
  const resultColumns = ref([]);
  const resultRows = ref([]);
  const total = ref(0);
  const truncated = ref(false);

  const runQuery = async () => {
    const text = sql.value;
    // A second run while one is in flight is ignored (the Run button shows its spinner).
    if (!props.query || running.value || !text.trim()) return;

    running.value = true;
    error.value = '';
    noRows.value = false;
    try {
      const { columns, rows } = toResult(await props.query.run(text));
      total.value = rows.length;
      truncated.value = rows.length > MAX_ROWS;
      resultRows.value = rows.slice(0, MAX_ROWS);
      resultColumns.value = rows.length > 0 ? columns : [];
      noRows.value = rows.length === 0;
    } catch (err) {
      console.error('DuckDB query error:', err);
      error.value = err?.message || String(err);
      resultColumns.value = [];
      resultRows.value = [];
      total.value = 0;
      truncated.value = false;
    } finally {
      running.value = false;
    }
  };

  return {
    // `emit` is a template binding here: the export button calls `emit('export')`
    // inline, exactly as it did when this was a script-setup SFC.
    emit,
    t,
    dialog,
    close,
    MAX_ROWS,
    sql,
    running,
    error,
    noRows,
    resultColumns,
    resultRows,
    total,
    truncated,
    runQuery,
    formatCell,
  };
};
